import { useMemo } from "react";
import { CheckCircle2, Info, XCircle } from "lucide-react";
import type { PageIssue } from "@/types/technicalAudit";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { buildIssueBeforeAfter } from "./issueBeforeAfter";
import { PlainBeforeAfter } from "./PlainBeforeAfter";

/** Shape of the verify-fix API response (technicalAuditService.verifyFix). */
export interface VerifyFixResult {
	success: boolean;
	fix_status?: string;
	reason?: string;
	result_status?: string;
	requires_manual_verification?: boolean;
	verification_mode?: string;
	evidence?: Record<string, unknown>;
}

interface VerifyResultModalProps {
	issue: PageIssue | null;
	verifyResult: VerifyFixResult | null;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

type VerifyOutcome = "verified" | "failed" | "manual" | "unsettled";

function resolveOutcome(result: VerifyFixResult | null): VerifyOutcome {
	if (!result) return "failed";
	if (
		measuredWordCount(result) !== null &&
		evidenceText(result, "threshold_verdict") === "inconclusive"
	) {
		return "unsettled";
	}
	if (result.result_status === "verified_fixed" || result.success) return "verified";
	if (
		result.result_status === "manual_verification_required" ||
		// The page could not be READ (WAF 403, timeout). That is inconclusive —
		// NOT "still detected". Falling through to "failed" told operators a
		// provably-live fix had failed (IWH, 2026-07-10) and sent them to
		// re-apply work that already succeeded.
		result.result_status === "verification_inconclusive" ||
		result.result_status === "not_ready"
	) {
		return "manual";
	}
	return "failed";
}

// Plain-English conclusion shown under the evidence line. Generic across issue
// types — the verb ("dead link", "issue") stays neutral so it reads sensibly
// for any issue_type.
function conclusionFor(outcome: VerifyOutcome, result: VerifyFixResult | null): string {
	if (outcome === "verified") {
		return (
			result?.reason ||
			"The issue no longer appears on the page — the fix is confirmed."
		);
	}
	if (outcome === "unsettled") {
		return (
			result?.reason ||
			"The page was re-read and counted, but the threshold uses the crawler's own counter, which this re-read does not reproduce."
		);
	}
	if (outcome === "manual") {
		return (
			result?.reason ||
			"Needs manual verification — confirm the change in WordPress or with a follow-up crawl."
		);
	}
	return result?.reason || "Still detected — the issue was not fixed.";
}

function evidenceUrl(result: VerifyFixResult | null): string | undefined {
	const url = result?.evidence?.url;
	return typeof url === "string" && url ? url : undefined;
}

function evidenceStatusCode(result: VerifyFixResult | null): number | undefined {
	const code = result?.evidence?.status_code;
	return typeof code === "number" ? code : undefined;
}

function evidenceNumber(result: VerifyFixResult | null, key: string): number | null {
	const value = result?.evidence?.[key];
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function evidenceText(result: VerifyFixResult | null, key: string): string | null {
	const value = result?.evidence?.[key];
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

function evidenceExecution(
	result: VerifyFixResult | null,
): Record<string, unknown> | null {
	const value = result?.evidence?.execution;
	return value && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: null;
}

function executionField(
	execution: Record<string, unknown> | null,
	key: string,
): string | null {
	const value = execution?.[key];
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

function measuredWordCount(result: VerifyFixResult | null): number | null {
	return (
		evidenceNumber(result, "verifier_word_count") ??
		evidenceNumber(result, "current_word_count")
	);
}

const COUNTER_LABEL: Record<string, string> = {
	screaming_frog: "Screaming Frog, the crawler",
};

function pageWasRead(result: VerifyFixResult | null): boolean {
	if (evidenceText(result, "verification_source") !== null) return true;
	const code = evidenceStatusCode(result);
	return typeof code === "number" && code >= 200 && code < 300;
}

const SOURCE_SENTENCE: Record<string, string> = {
	wp_content_raw: "Read from the WordPress post body (content.raw).",
	http_refetch: "Read from a fresh fetch of the published page.",
};

const EXECUTION_SOURCE_LABEL: Record<string, string> = {
	emjae_chat: "chat settlement",
};

function appliedAt(
	issue: PageIssue | null,
	execution: Record<string, unknown> | null,
): string | null {
	const recorded = executionField(execution, "applied_at");
	if (recorded) return recorded;
	if (execution) return "";
	if (!issue) return null;
	if (issue.applied_at) return issue.applied_at;
	return issue.fix_status === "applied" ? "" : null;
}

function formatWhen(value: string): string | null {
	if (!value) return null;
	const at = Date.parse(value);
	if (!Number.isFinite(at)) return null;
	return new Date(at).toLocaleString();
}

const OUTCOME_META: Record<
	VerifyOutcome,
	{
		title: string;
		badge: string;
		Icon: typeof CheckCircle2;
		badgeClass: string;
		iconClass: string;
	}
> = {
	verified: {
		title: "Fix verified",
		badge: "Verified",
		Icon: CheckCircle2,
		badgeClass: "border-teal/40 bg-teal-light text-teal-dark",
		iconClass: "text-teal-dark",
	},
	failed: {
		title: "Verification failed",
		badge: "Still detected",
		Icon: XCircle,
		badgeClass: "border-destructive/40 bg-destructive/10 text-destructive",
		iconClass: "text-destructive",
	},
	unsettled: {
		title: "Page re-read \u2014 threshold not settled",
		badge: "Needs a fresh crawl",
		Icon: Info,
		badgeClass: "border-coral/40 bg-coral-light text-coral-dark",
		iconClass: "text-coral-dark",
	},
	manual: {
		title: "Manual verification required",
		badge: "Manual review",
		Icon: Info,
		badgeClass: "border-coral/40 bg-coral-light text-coral-dark",
		iconClass: "text-coral-dark",
	},
};

export function VerifyResultModal({
	issue,
	verifyResult,
	open,
	onOpenChange,
}: VerifyResultModalProps) {
	const outcome = resolveOutcome(verifyResult);
	const meta = OUTCOME_META[outcome];
	const { Icon } = meta;

	const diff = useMemo(
		() =>
			issue
				? buildIssueBeforeAfter(issue, issue.approved_value ?? issue.proposed_fix ?? undefined)
				: { before: "Not available", after: "Not available" },
		[issue],
	);

	const url = evidenceUrl(verifyResult);
	const statusCode = evidenceStatusCode(verifyResult);
	const conclusion = conclusionFor(outcome, verifyResult);
	const fetched = pageWasRead(verifyResult);
	const execution = evidenceExecution(verifyResult);
	const applied = appliedAt(issue, execution);
	const appliedWhen = applied ? formatWhen(applied) : null;
	const executionActor = executionField(execution, "actor");
	const executionSourceRaw = executionField(execution, "source");
	const executionSource = executionSourceRaw
		? (EXECUTION_SOURCE_LABEL[executionSourceRaw] ?? executionSourceRaw)
		: null;
	const currentWords = measuredWordCount(verifyResult);
	const threshold = evidenceNumber(verifyResult, "threshold");
	const crawledWords = evidenceNumber(verifyResult, "crawled_word_count");
	const countMethod = evidenceText(verifyResult, "word_count_method");
	const sourceKey = evidenceText(verifyResult, "verification_source");
	const sourceSentence = sourceKey ? SOURCE_SENTENCE[sourceKey] : undefined;
	const thresholdCounterRaw = evidenceText(verifyResult, "threshold_counter");
	const thresholdCounter = thresholdCounterRaw
		? (COUNTER_LABEL[thresholdCounterRaw] ?? thresholdCounterRaw)
		: null;
	const thresholdComparable =
		evidenceText(verifyResult, "threshold_verdict") !== "inconclusive";
	const paragraphsPresent = evidenceNumber(verifyResult, "applied_paragraphs_present");
	const paragraphsTotal = evidenceNumber(verifyResult, "applied_paragraphs_total");
	const measured = currentWords !== null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<Icon className={`h-5 w-5 shrink-0 ${meta.iconClass}`} aria-hidden />
						<DialogTitle>{meta.title}</DialogTitle>
						<span
							data-testid="verify-result-badge"
							className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.badgeClass}`}
						>
							{meta.badge}
						</span>
					</div>
					<DialogDescription>
						{fetched
							? `Re-checked ${issue?.page_url || "the page"} just now.`
							: `The page at ${
									issue?.page_url || "this URL"
								} was not read, so nothing here shows its current state.`}
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* Evidence line + plain-English conclusion. */}
					<div
						data-testid="verify-result-evidence"
						className="rounded-md border border-border bg-warm-bg p-3 text-sm"
					>
						{(url || typeof statusCode === "number") && (
							<div className="mb-1 break-words font-mono text-xs text-muted-foreground">
								{url}
								{typeof statusCode === "number" ? (
									<span className="ml-2 font-semibold text-foreground">
										HTTP {statusCode}
									</span>
								) : null}
							</div>
						)}
						<div className="text-foreground/90">{conclusion}</div>
					</div>

					{applied !== null && (
						<div
							data-testid="verify-result-applied-note"
							className="rounded-md border border-border bg-warm-bg p-3 text-sm"
						>
							<div className="font-medium text-foreground">
								A change was already applied to this page
							</div>
							<div className="mt-0.5 text-muted-foreground">
								{appliedWhen
									? `Recorded as applied on ${appliedWhen}`
									: "Recorded as applied"}
								{executionActor ? ` by ${executionActor}` : ""}
								{executionSource ? ` (${executionSource})` : ""}.{" "}
								{measured
									? "The measurement below was taken from the page after that change."
									: "This result does not include a measurement of the page after that change."}
							</div>
						</div>
					)}

					{measured ? (
						<div
							data-testid="verify-result-measurement"
							className="rounded-md border border-border bg-warm-bg p-3 text-sm"
						>
							<div className="font-medium text-foreground">
								{threshold !== null && thresholdComparable
									? `${currentWords} words on the page now, against a ${threshold}-word threshold.`
									: `${currentWords} words on the page now.`}
							</div>
							<dl className="mt-2 space-y-1 text-xs text-muted-foreground">
								{sourceSentence || sourceKey ? (
									<div>
										<dt className="inline font-medium text-foreground">Source: </dt>
										<dd className="inline">{sourceSentence ?? sourceKey}</dd>
									</div>
								) : null}
								{countMethod ? (
									<div>
										<dt className="inline font-medium text-foreground">
											How it was counted:{" "}
										</dt>
										<dd className="inline">{countMethod}</dd>
									</div>
								) : null}
								{paragraphsTotal !== null ? (
									<div>
										<dt className="inline font-medium text-foreground">
											Applied paragraphs:{" "}
										</dt>
										<dd className="inline">
											{`${paragraphsPresent ?? 0} of ${paragraphsTotal} found on the page.`}
										</dd>
									</div>
								) : null}
								{threshold !== null && !thresholdComparable ? (
									<div>
										<dt className="inline font-medium text-foreground">
											Threshold:{" "}
										</dt>
										<dd className="inline">
											{`${threshold} words, counted by ${
												thresholdCounter ?? "the crawler"
											}. This re-read does not reproduce that counter, so the threshold verdict needs a fresh crawl.`}
										</dd>
									</div>
								) : null}
								{crawledWords !== null ? (
									<div>
										<dt className="inline font-medium text-foreground">
											At crawl:{" "}
										</dt>
										<dd className="inline">
											{`${crawledWords} words. That figure came from the crawler's own counter, so it is not directly comparable with the count above.`}
										</dd>
									</div>
								) : null}
							</dl>
						</div>
					) : applied !== null ? null : (
						<div className="space-y-2">
							{!fetched ? (
								<div className="text-xs text-muted-foreground">
									These figures come from the stored audit record. Nothing here has been measured against the live page.
								</div>
							) : null}
							<PlainBeforeAfter
								before={diff.before}
								after={diff.after}
								beforeLabel={fetched ? "Current" : "At audit time"}
								afterLabel="Proposed"
								beforeTestId="verify-result-before"
								afterTestId="verify-result-after"
							/>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
